// src/country.ts
import { SUPPORTED_COUNTRIES } from "@klareospace/pharos-lib-types";
var COUNTRIES = [
  { alpha2: "AT", code: "AUT", name: "Austria" },
  { alpha2: "BE", code: "BEL", name: "Belgium" },
  { alpha2: "CA", code: "CAN", name: "Canada" },
  { alpha2: "CH", code: "CHE", name: "Switzerland" },
  { alpha2: "DE", code: "DEU", name: "Germany" },
  { alpha2: "DZ", code: "DZA", name: "Algeria" },
  { alpha2: "ES", code: "ESP", name: "Spain" },
  { alpha2: "FR", code: "FRA", name: "France" },
  { alpha2: "GB", code: "GBR", name: "United Kingdom" },
  { alpha2: "IT", code: "ITA", name: "Italy" },
  { alpha2: "LB", code: "LBN", name: "Lebanon" },
  { alpha2: "LU", code: "LUX", name: "Luxembourg" },
  { alpha2: "MA", code: "MAR", name: "Morocco" },
  { alpha2: "NL", code: "NLD", name: "Netherlands" },
  { alpha2: "PT", code: "PRT", name: "Portugal" },
  { alpha2: "TN", code: "TUN", name: "Tunisia" },
  { alpha2: "US", code: "USA", name: "United States" }
];
var COUNTRY_CODE_TO_NAME = new Map(COUNTRIES.map((c) => [c.code, c.name]));
var COUNTRY_ALPHA2_TO_NAME = new Map(COUNTRIES.map((c) => [c.alpha2, c.name]));
var COUNTRY_NAME_TO_NAME = new Map(COUNTRIES.map((c) => [c.name.toLowerCase(), c.name]));
var COUNTRY_NAME_TO_CODE = new Map(COUNTRIES.map((c) => [c.name.toLowerCase(), c.code]));
var COUNTRY_CODE_TO_ALPHA2 = new Map(COUNTRIES.map((c) => [c.code, c.alpha2]));
function getCountryNameFromCode(code) {
  return COUNTRY_CODE_TO_NAME.get(code.toUpperCase());
}
function toCountryName(value) {
  return getCountryNameFromCode(value) ?? value;
}
function normalizeCountry(value) {
  if (!value) {
    return value;
  }
  const upper = value.toUpperCase();
  return COUNTRY_CODE_TO_NAME.get(upper) ?? COUNTRY_ALPHA2_TO_NAME.get(upper) ?? COUNTRY_NAME_TO_NAME.get(value.toLowerCase()) ?? value;
}
function normalizeCountryCode(value) {
  if (!value) return "";
  if (COUNTRY_CODE_TO_NAME.has(value)) return value;
  return COUNTRY_NAME_TO_CODE.get(value.toLowerCase()) ?? value;
}
function isSupportedCountry(value) {
  return SUPPORTED_COUNTRIES.includes(value);
}
function toSupportedCountry(value) {
  if (!value) return void 0;
  const upper = value.toUpperCase();
  const alpha2 = COUNTRY_CODE_TO_ALPHA2.get(upper) ?? (COUNTRY_ALPHA2_TO_NAME.has(upper) ? upper : void 0);
  return alpha2 && isSupportedCountry(alpha2) ? alpha2 : void 0;
}

// src/address.ts
function formatPostalAddress(address) {
  if (!address) {
    return "";
  }
  const streetPart = [address.houseNumber, address.street].map((part) => part?.trim() ?? "").filter(Boolean).join(" ");
  const cityPart = [address.postalCode, address.city].map((part) => part?.trim() ?? "").filter(Boolean).join(" ");
  const countryPart = address.country?.trim();
  return [streetPart, cityPart, countryPart ? toCountryName(countryPart) : void 0].filter(Boolean).join(", ");
}
function resolveDeliveryPostalAddress(address) {
  if (!address) {
    return void 0;
  }
  if ("location" in address) {
    return address.location?.address;
  }
  return address;
}
function resolveDeliveryCoordinates(address) {
  if (!address) {
    return void 0;
  }
  if ("location" in address) {
    return address.location?.coordinates;
  }
  return void 0;
}
function formatDeliveryAddress(address) {
  if (!address) {
    return "";
  }
  const formattedPostalAddress = formatPostalAddress(resolveDeliveryPostalAddress(address));
  if (formattedPostalAddress) {
    return formattedPostalAddress;
  }
  if ("originalAddress" in address) {
    const trimmedOriginal = address.originalAddress?.trim();
    if (trimmedOriginal) {
      return trimmedOriginal;
    }
  }
  if ("title" in address) {
    const trimmedTitle = address.title?.trim();
    if (trimmedTitle) {
      return trimmedTitle;
    }
  }
  return "";
}
function formatRequestedAddress(address) {
  if (!address) {
    return "";
  }
  const trimmedFull = address.full?.trim();
  if (trimmedFull) {
    return trimmedFull;
  }
  return formatPostalAddress(address);
}
function normalizeAddressForMatch(value) {
  return (value ?? "").toLowerCase().replace(/[.,]/g, " ").replace(/\s+/g, " ").trim();
}

// src/api-response.ts
function createSuccessResponse(data) {
  return {
    data,
    success: true
  };
}
function isApiError(response) {
  return !response.success;
}
function getApiData(response) {
  return response.success ? response.data : void 0;
}

// src/constants.ts
var DEFAULT_GC_TIME_MS = 10 * 60 * 1e3;
var DEFAULT_STALE_TIME_MS = 5 * 60 * 1e3;
var LIST_STALE_TIME_MS = 3e4;
var LIST_GC_TIME_MS = 5 * 6e4;

// src/carrier.ts
function isCarrierEnabledOnAnySiteWithinSpace(enabledScopes, spaceId, totalSitesInSpace) {
  const scope = enabledScopes.find((s) => s.spaceId === spaceId);
  if (!scope) return false;
  const specifics = scope.siteSpecifics ?? [];
  if (scope.enabledOnAllSites) {
    if (totalSitesInSpace === 0) return false;
    if (specifics.length === 0) return true;
    const disabledCount = specifics.filter((ss) => ss.disabled).length;
    return disabledCount < totalSitesInSpace;
  }
  return specifics.some((ss) => !ss.disabled);
}
function isCarrierReferencedInSpace(enabledScopes, spaceId) {
  return enabledScopes.some((s) => s.spaceId === spaceId);
}
function isCarrierFullyEnabledOnSpace(enabledScopes, spaceId) {
  const scope = enabledScopes.find((s) => s.spaceId === spaceId);
  if (!scope?.enabledOnAllSites) return false;
  const specifics = scope.siteSpecifics ?? [];
  return !specifics.some((ss) => ss.disabled);
}
function isCarrierEnabledOnSite(enabledScopes, siteId, spaceId) {
  const scope = enabledScopes.find((s) => s.spaceId === spaceId);
  if (!scope) return false;
  const specifics = scope.siteSpecifics ?? [];
  const siteSpecific = specifics.find((ss) => ss.siteId === siteId);
  if (scope.enabledOnAllSites) {
    return siteSpecific?.disabled !== true;
  }
  return siteSpecific !== void 0 && siteSpecific.disabled !== true;
}

// src/string.ts
import { PharosGlobalConfiguration } from "@klareospace/pharos-lib-types";
function toKebabCase(value) {
  return value.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}
function computeInitials(name, fallback = "??") {
  if (!name?.trim()) {
    return fallback;
  }
  return name.trim().split(/\s+/).map((part) => part[0]).join("").toUpperCase().slice(0, 2);
}
function getFullName(firstName, lastName) {
  return [firstName, lastName].map((part) => part?.trim()).filter(Boolean).join(" ");
}
function getDisplayName(user) {
  if (!user) return void 0;
  const full = getFullName(user.firstName, user.lastName);
  return full || void 0;
}
function resolveDriverName(driver) {
  return getDisplayName(driver ?? void 0);
}
function buildEscapedRegExp(value, caseInsensitive = true) {
  return new RegExp(value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), caseInsensitive ? "i" : "");
}
var REGEX_METACHARACTER = /[.*+?^${}()|[\]\\]/;
var ACCENT_CLASSES = {
  a: "a\xE0\xE1\xE2\xE3\xE4\xE5\u0101\u0103\u0105",
  c: "c\xE7\u0107\u0109\u010B\u010D",
  e: "e\xE8\xE9\xEA\xEB\u0113\u0115\u0117\u0119\u011B",
  i: "i\xEC\xED\xEE\xEF\u0129\u012B\u012D\u012F\u0131",
  n: "n\xF1\u0144\u0146\u0148\u0149",
  o: "o\xF2\xF3\xF4\xF5\xF6\xF8\u014D\u014F\u0151",
  s: "s\u015B\u015D\u015F\u0161",
  u: "u\xF9\xFA\xFB\xFC\u0169\u016B\u016D\u016F\u0171\u0173",
  y: "y\xFD\xFF\u0177",
  z: "z\u017A\u017C\u017E"
};
var ACCENT_CLASSES_CASED = Object.fromEntries(
  Object.entries(ACCENT_CLASSES).map(([base, lowerChars]) => [base, lowerChars + lowerChars.toUpperCase()])
);
var ACCENT_TO_BASE = new Map(
  Object.entries(ACCENT_CLASSES).flatMap(
    ([base, accentedChars]) => Array.from(accentedChars).map((char) => [char, base])
  )
);
function buildAccentInsensitiveRegExp(value) {
  const pattern = Array.from(value.normalize("NFC")).map((char) => buildAccentFoldedCharPattern(char)).join("");
  return new RegExp(pattern, "i");
}
function buildAccentFoldedCharPattern(char) {
  if (REGEX_METACHARACTER.test(char)) {
    return `\\${char}`;
  }
  const base = ACCENT_TO_BASE.get(char.toLowerCase());
  return base ? `[${ACCENT_CLASSES_CASED[base]}]` : char;
}
function normalizeEmail(email) {
  return email.trim().toLowerCase();
}
function isInternalEmail(email) {
  const normalized = normalizeEmail(email);
  return PharosGlobalConfiguration.auth.internalEmailDomains.some((domain) => normalized.endsWith(domain));
}
function humanize(value) {
  return value.split(/[-_]/).map((word) => {
    const lower = word.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }).join(" ");
}
function joinStrings(values, separator = ", ") {
  if (values === void 0 || values.length === 0) return "";
  return values.join(separator);
}
var DEFAULT_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
var NANOID_ALPHABET = "useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict";
var SHORT_ID_LENGTH = 21;
function getCryptoApi() {
  return typeof globalThis !== "undefined" && "crypto" in globalThis ? globalThis.crypto : void 0;
}
function generateRandomString(length, alphabet = DEFAULT_ALPHABET) {
  const cryptoApi = getCryptoApi();
  let result = "";
  if (cryptoApi?.getRandomValues) {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(length));
    for (let i = 0; i < length; i++) result += alphabet[bytes[i] % alphabet.length];
  } else {
    for (let i = 0; i < length; i++) result += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return result;
}
function generateId() {
  const cryptoApi = getCryptoApi();
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : r & 3 | 8;
    return v.toString(16);
  });
}
function generateShortId() {
  return generateRandomString(SHORT_ID_LENGTH, NANOID_ALPHABET);
}

// src/contact.ts
function buildCustomerDefaultContact(customer) {
  const contact = { name: getFullName(customer.firstName, customer.lastName) };
  if (customer.phone) {
    contact.phone = customer.phone;
  }
  return contact;
}
function isSameContact(a, b) {
  if (!a || !b) {
    return false;
  }
  return a.name === b.name && a.phone?.dialCode === b.phone?.dialCode && a.phone?.number === b.phone?.number;
}

// src/date.ts
var FAR_FUTURE_DATE = /* @__PURE__ */ new Date("9999-12-31T23:59:59.999Z");
function isInvertedTimeSlot(slot) {
  return new Date(slot.end).getTime() <= new Date(slot.start).getTime();
}
function toDate(value) {
  if (!value) return void 0;
  return value instanceof Date ? value : new Date(value);
}
function getStartOfToday() {
  const d = /* @__PURE__ */ new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}
function lastDayOfMonthUtc(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

// src/jwt.ts
function decodeJwtPayload(token) {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, "=");
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}
function isPharosJWTToken(value) {
  if (typeof value !== "object" || value === null) return false;
  if (!("tenantId" in value)) return false;
  if (typeof value.tenantId !== "string") return false;
  if (!("userId" in value)) return false;
  if (typeof value.userId !== "string") return false;
  return true;
}

// src/kpi-period.ts
function periodEnd(bound) {
  return new Date(Date.UTC(bound.year, bound.month - 1, (bound.day ?? 1) + 1));
}
function periodToDateRange(from, to) {
  return {
    fromDate: new Date(Date.UTC(from.year, from.month - 1, from.day ?? 1)),
    toDateExclusive: periodEnd(to)
  };
}
function isDateInPeriod(from, to, date) {
  const { fromDate, toDateExclusive } = periodToDateRange(from, to);
  return date.getTime() >= fromDate.getTime() && date.getTime() < toDateExclusive.getTime();
}
function toUtcDayBound(date) {
  return {
    day: date.getUTCDate(),
    month: date.getUTCMonth() + 1,
    year: date.getUTCFullYear()
  };
}
function toMtdToBound(year, month, now) {
  const todayUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const monthStart = new Date(Date.UTC(year, month - 1, 1));
  if (todayUtc.getTime() < monthStart.getTime()) {
    return void 0;
  }
  const monthEnd = new Date(Date.UTC(year, month - 1, lastDayOfMonthUtc(year, month)));
  const bound = todayUtc.getTime() < monthEnd.getTime() ? todayUtc : monthEnd;
  return toUtcDayBound(bound);
}

// src/phone.ts
import { parsePhoneNumberFromString } from "libphonenumber-js";
function normalizeDialCode(dialCode) {
  const digits = stripNonDigits(dialCode);
  return digits ? `+${digits}` : dialCode.trim();
}
function normalizeNationalNumber(number, dialCode) {
  return resolvePhoneNumber(dialCode, number).number;
}
function normalizePhoneNumber(phone) {
  return resolvePhoneNumber(phone.dialCode ?? "", phone.number ?? "");
}
function parseValidPhoneNumber(dialCode, number) {
  const parsed = parseToLibPhoneNumber(dialCode, number);
  if (!parsed?.isValid()) {
    return void 0;
  }
  return { dialCode: `+${parsed.countryCallingCode}`, number: parsed.nationalNumber };
}
function stripNonDigits(value) {
  return value.replace(/\D/g, "");
}
function parseToLibPhoneNumber(dialCode, number) {
  const dialDigits = stripNonDigits(dialCode);
  const numberDigits = stripNonDigits(number);
  return dialDigits && numberDigits ? parsePhoneNumberFromString(`+${dialDigits}${numberDigits}`) : void 0;
}
function resolvePhoneNumber(dialCode, number) {
  const parsed = parseToLibPhoneNumber(dialCode, number);
  if (parsed?.isValid()) {
    return { dialCode: `+${parsed.countryCallingCode}`, number: parsed.nationalNumber };
  }
  const numberDigits = stripNonDigits(number);
  return {
    dialCode: normalizeDialCode(dialCode),
    number: numberDigits || number.trim()
  };
}

// src/scope.ts
import { ROLE_SCOPE } from "@klareospace/pharos-lib-types";

// src/utils/array.ts
function deduplicate(items) {
  return Array.from(new Set(items));
}
function insertAt(array, index, item) {
  return [...array.slice(0, index), item, ...array.slice(index)];
}
function isArrayResult(value) {
  return Array.isArray(value);
}
function uniformValue(values) {
  const first = values[0];
  if (first === void 0) {
    return void 0;
  }
  return values.every((value) => value === first) ? first : void 0;
}

// src/utils/exhaustive.ts
function assertNever(value) {
  throw new Error(`Unhandled case: ${JSON.stringify(value)}`);
}

// src/utils/format-build-label.ts
var COMMIT_SHA_LABEL_LENGTH = 7;
var UPDATE_ID_LABEL_LENGTH = 8;
function formatBuildLabel(parts) {
  const { buildNumber, commitSha, updateId, version } = parts;
  const segments = [`v${version}`];
  if (buildNumber) {
    segments.push(`(${buildNumber})`);
  }
  if (commitSha) {
    segments.push(`- ${commitSha.slice(0, COMMIT_SHA_LABEL_LENGTH)}`);
  }
  if (updateId) {
    segments.push(`\xB7 ${updateId.slice(0, UPDATE_ID_LABEL_LENGTH)}`);
  }
  return segments.join(" ");
}

// src/utils/number.ts
function ratioToPercent(ratio, decimals) {
  const percent = ratio * 100;
  return decimals === void 0 ? percent : Number(percent.toFixed(decimals));
}

// src/utils/object.ts
function pickExcept(source, keys) {
  const result = {};
  for (const [key, value] of Object.entries(source)) {
    if (!keys.includes(key)) {
      result[key] = value;
    }
  }
  return result;
}
function keepDefined(source) {
  const result = {};
  for (const [key, value] of Object.entries(source)) {
    if (value !== void 0 && value !== null) {
      result[key] = value;
    }
  }
  return result;
}
function isDefined(value) {
  return value !== null && value !== void 0;
}
function isNonEmptyValue(value) {
  return Array.isArray(value) ? value.length > 0 : isDefined(value);
}

// src/utils/pagination.ts
function toPaginatedResponse(data) {
  if (data.pageSize <= 0) {
    throw new Error(`[toPaginatedResponse] pageSize must be > 0, received '${data.pageSize}'`);
  }
  return {
    items: data.items,
    page: data.page,
    pageSize: data.pageSize,
    total: data.total,
    totalPages: Math.ceil(data.total / data.pageSize)
  };
}

// src/utils/prune-empty-objects.ts
function pruneEmptyObjects(value) {
  if (Array.isArray(value) || value === null || typeof value !== "object" || value instanceof Date) {
    return value;
  }
  const result = {};
  for (const [key, rawValue] of Object.entries(value)) {
    const prunedValue = pruneEmptyObjects(rawValue);
    if (!isEmptyPlainObject(prunedValue)) {
      result[key] = prunedValue;
    }
  }
  return result;
}
function isEmptyPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date) && Object.keys(value).length === 0;
}

// src/utils/race-with-timeout.ts
async function raceWithTimeout(promise, timeoutMs, fallback) {
  let timeoutId;
  const timeout = new Promise((resolve) => {
    timeoutId = setTimeout(() => resolve(fallback), timeoutMs);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(timeoutId);
  }
}

// src/utils/retry.ts
var DEFAULT_RETRY_DELAY_MS = 1e3;
var DEFAULT_MAX_RETRIES = 3;
async function retryWithDelay(fn, options) {
  const { delayMs = DEFAULT_RETRY_DELAY_MS, maxRetries = DEFAULT_MAX_RETRIES } = options ?? {};
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
  }
  throw lastError;
}

// src/utils/subscribable.ts
function createSubscribable() {
  const listeners = /* @__PURE__ */ new Set();
  return {
    notify() {
      listeners.forEach((listener) => listener());
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    }
  };
}

// src/scope.ts
function getScopeIdsByLevel(scopes, level) {
  return scopes.filter((s) => s.level === level).map((s) => s.id);
}
function getScopesByLevel(scopes, level) {
  return scopes.filter((s) => s.level === level);
}
function buildScopesFromIds(spaceIds, siteIds) {
  const spaceScopes = spaceIds.map((id) => ({ id, level: ROLE_SCOPE.SPACE }));
  const siteScopes = siteIds.map((id) => ({ id, level: ROLE_SCOPE.SITE }));
  return [...spaceScopes, ...siteScopes];
}
function getParentSpaceIdsFromResolvedScopes(resolvedScopes) {
  const parentIds = resolvedScopes.filter((s) => s.level === ROLE_SCOPE.SITE && !!s.parentSpaceId).map((s) => s.parentSpaceId);
  return deduplicate(parentIds);
}
function getSpaceLabelsFromResolvedScopes(resolvedScopes) {
  const labels = /* @__PURE__ */ new Map();
  for (const scope of resolvedScopes) {
    if (scope.level === ROLE_SCOPE.SPACE && scope.name) {
      labels.set(scope.id, scope.name);
    }
    if (scope.level === ROLE_SCOPE.SITE && scope.parentSpaceId && scope.parentSpaceName) {
      labels.set(scope.parentSpaceId, scope.parentSpaceName);
    }
  }
  return labels;
}

// src/task.ts
import {
  TASK_JOB_TYPE
} from "@klareospace/pharos-lib-types";
function findDeliveryJob(task) {
  return task.jobs.find((job) => job.type === TASK_JOB_TYPE.DELIVERY);
}
function findPickupJob(task) {
  return task.jobs.find((job) => job.type === TASK_JOB_TYPE.PICKUP);
}
function resolveTaskTimeSlot(task) {
  return findDeliveryJob(task)?.data.timeSlot;
}

// src/template.ts
function substituteString(template, values) {
  return template.replace(/\{\{values\.([^}]+)\}\}/g, (_match, key) => {
    const val = values[key];
    if (val === void 0 || val === null) {
      return "";
    }
    return String(val);
  });
}
function substituteRecord(record, values) {
  const result = {};
  for (const [k, v] of Object.entries(record)) {
    result[k] = substituteString(v, values);
  }
  return result;
}
export {
  COUNTRIES,
  DEFAULT_GC_TIME_MS,
  DEFAULT_STALE_TIME_MS,
  FAR_FUTURE_DATE,
  LIST_GC_TIME_MS,
  LIST_STALE_TIME_MS,
  assertNever,
  buildAccentInsensitiveRegExp,
  buildCustomerDefaultContact,
  buildEscapedRegExp,
  buildScopesFromIds,
  computeInitials,
  createSubscribable,
  createSuccessResponse,
  decodeJwtPayload,
  deduplicate,
  findDeliveryJob,
  findPickupJob,
  formatBuildLabel,
  formatDeliveryAddress,
  formatPostalAddress,
  formatRequestedAddress,
  generateId,
  generateRandomString,
  generateShortId,
  getApiData,
  getCountryNameFromCode,
  getDisplayName,
  getFullName,
  getParentSpaceIdsFromResolvedScopes,
  getScopeIdsByLevel,
  getScopesByLevel,
  getSpaceLabelsFromResolvedScopes,
  getStartOfToday,
  humanize,
  insertAt,
  isApiError,
  isArrayResult,
  isCarrierEnabledOnAnySiteWithinSpace,
  isCarrierEnabledOnSite,
  isCarrierFullyEnabledOnSpace,
  isCarrierReferencedInSpace,
  isDateInPeriod,
  isDefined,
  isInternalEmail,
  isInvertedTimeSlot,
  isNonEmptyValue,
  isPharosJWTToken,
  isSameContact,
  joinStrings,
  keepDefined,
  lastDayOfMonthUtc,
  normalizeAddressForMatch,
  normalizeCountry,
  normalizeCountryCode,
  normalizeDialCode,
  normalizeEmail,
  normalizeNationalNumber,
  normalizePhoneNumber,
  parseValidPhoneNumber,
  periodEnd,
  periodToDateRange,
  pickExcept,
  pruneEmptyObjects,
  raceWithTimeout,
  ratioToPercent,
  resolveDeliveryCoordinates,
  resolveDeliveryPostalAddress,
  resolveDriverName,
  resolveTaskTimeSlot,
  retryWithDelay,
  substituteRecord,
  substituteString,
  toCountryName,
  toDate,
  toKebabCase,
  toMtdToBound,
  toPaginatedResponse,
  toSupportedCountry,
  toUtcDayBound,
  uniformValue
};
//# sourceMappingURL=index.mjs.map